"use client"
import { AnalystsPickTag } from "@/components/AnalystsPickTag"
import { randomStatColor } from "@/lib/random-stat-color"
import { createAssetUrl } from "@/lib/s3"

import { cn } from "@/lib/tailwind"
import { CloudDownload, Folder, FolderCheck, Share } from "lucide-react"
import NextLink from "next/link"
import * as React from "react"
import { ContentIcon } from "../ContentIcon"
import { DownloadFile } from "../DownloadFile"
import { ShareContent } from "../ShareContent"
import { SaveToLibrary } from "../save-to-library"
import { Tooltip } from "../ui/Tooltip"
import type { StatItemType } from "./fields"
import { IconButton } from "../ui/IconButton"

interface Props {
  stat: StatItemType
  size?: "sm" | "md"
}

export const StatItem = React.memo(function _StatItem({ stat, size = "md" }: Props) {
  const bgColor = React.useMemo(randomStatColor, [])
  const isSaved = stat.projectItems.length > 0
  return (
    <div className={cn("flex h-full flex-col justify-between rounded-lg p-4 pb-2 text-black", bgColor)} suppressHydrationWarning>
      <div className="space-y-1">
        <div className="flex w-full justify-between">
          <NextLink prefetch={false} href={`/stats/${stat.id}`}>
            <p className="w-full font-bold text-7xl leading-none hover:opacity-80">{stat.title}</p>
          </NextLink>
          {stat.isAnalystPick && size === "md" && (
            <div>
              <AnalystsPickTag size="sm" avatar={createAssetUrl(stat.analyst?.avatar)} user={stat.analyst} />
            </div>
          )}
        </div>
        <NextLink prefetch={false} href={`/stats/${stat.id}`}>
          <p className="font-medium hover:opacity-80">{stat.subTitle}</p>
        </NextLink>
        <a className="text-sm" target="_blank" rel="noopener noreferrer" href={stat.sourceLink || undefined}>
          {stat.source}
        </a>
      </div>
      <div className="mt-2 space-y-0">
        <div className="flex justify-between space-x-2">
          <div className="flex flex-wrap">
            {stat.megatrends.map((megatrend) => (
              <Tooltip key={megatrend.id} label={`Mega-Trend: ${megatrend.title}`}>
                <NextLink prefetch={false} href={`/megatrends/${megatrend.slug}`}>
                  <ContentIcon bgColor={megatrend.color} icon={createAssetUrl(megatrend.iconImage)} className="p-1" />
                </NextLink>
              </Tooltip>
            ))}
          </div>
        </div>
        <div className="flex w-full items-center justify-between space-x-1">
          <NextLink prefetch={false} href={`/stats/${stat.id}`} className="flex items-center space-x-1 truncate">
            {stat.regions.length === 0 ? (
              <span className="text-xs opacity-80 lg:text-xs">GLOBAL</span>
            ) : (
              <Tooltip
                label={stat.regions
                  .slice(0, 10)
                  .map((r) => r.title)
                  .join(", ")}
                // isDisabled={stat.regions.length < 2}
              >
                <span className="truncate text-xs uppercase opacity-80 lg:text-xs">{stat.regions[0]?.title}</span>
              </Tooltip>
            )}

            {stat.industries[0] && (
              <>
                <span className="pb-1">·</span>
                <span className="truncate text-xs uppercase lg:text-xs">{stat.industries[0].title}</span>
              </>
            )}
          </NextLink>
          <div className="hidden md:flex">
            <ShareContent
              type={"STAT"}
              title={stat.title}
              subTitle={stat.subTitle}
              redirectUrl={`/stats/${stat.id}`}
              resourceId={stat.id}
            >
              <IconButton
                // tooltipProps={{ label: "Share" }}
                size="sm"
                variant="ghost"
                aria-label="share"
                icon={<Share size={16} className="text-black" />}
              />
            </ShareContent>
            <SaveToLibrary resourceId={stat.id} type="STAT" content={stat.title}>
              <IconButton
                // tooltipProps={{ label: "Save to project" }}
                size="sm"
                variant="ghost"
                aria-label="save"
                icon={isSaved ? <FolderCheck size={16} className="text-black" /> : <Folder size={16} className="text-black" />}
              />
            </SaveToLibrary>
            <DownloadFile pdf={stat.downloadPdf} ppt={stat.downloadPpt} content={stat.title} resourceId={stat.id} resource="STAT">
              <IconButton
                // tooltipProps={{ label: "Download" }}
                variant="ghost"
                size="sm"
                aria-label="download"
                icon={<CloudDownload size={16} className="text-black" />}
              />
            </DownloadFile>
          </div>
        </div>
      </div>
    </div>
  )
})
